import { Action, createReducer, on } from '@ngrx/store';

import { Notification } from '../../interfaces/notification.interface';
import { NotificationActions } from '../actions/notification.actions';

export interface NotificationsState {
  error: unknown;
  pending: boolean;
  read: number;
  unread: number;
  total: number;
  items: Notification[];
}

export const initialState: NotificationsState = {
  error: null,
  pending: false,
  read: 0,
  unread: 0,
  total: 0,
  items: [],
};

const notificationsReducer = createReducer(
  initialState,
  on(
    NotificationActions.load,
    (state): NotificationsState => ({ ...state, pending: true }),
  ),
  on(
    NotificationActions.loadSuccess,
    (state, { data }): NotificationsState => ({
      ...state,
      read: data.read,
      unread: data.unread,
      total: data.total,
      items: data.items,
      error: null,
      pending: false,
    }),
  ),
  on(
    NotificationActions.loadFailure,
    (state, { error }): NotificationsState => ({
      ...state,
      error,
      pending: false,
    }),
  ),
  on(NotificationActions.markRead, (state, { id }): NotificationsState => {
    const notification = state.items.find((item) => item.id === id);

    if (!notification || notification.readAt) {
      return state;
    }

    return {
      ...state,
      unread: Math.max(state.unread - 1, 0),
      read: state.read + 1,
      items: state.items.map((item) =>
        item.id === id
          ? { ...item, readAt: Math.floor(Date.now() / 1000) }
          : item,
      ),
    };
  }),
  on(
    NotificationActions.markReadFailure,
    (state, { error }): NotificationsState => ({
      ...state,
      error,
    }),
  ),
  on(
    NotificationActions.markAllReadSuccess,
    (state, { data }): NotificationsState => {
      const updatedById = new Map(data.items.map((item) => [item.id, item]));

      const items = state.items.map(
        (item) => updatedById.get(item.id) ?? item,
      );
      const unread = items.filter((item) => !item.readAt).length;

      return {
        ...state,
        items,
        unread,
        read: state.total - unread,
        error: null,
      };
    },
  ),
  on(
    NotificationActions.markAllReadFailure,
    (state, { error }): NotificationsState => ({
      ...state,
      error,
    }),
  ),
);

export const reducer = (
  state: NotificationsState | undefined,
  action: Action,
): NotificationsState => notificationsReducer(state, action);
